import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { HeaderLogo } from 'components/atoms/logos'
import PropTypes from 'prop-types'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { getScheduleStops } from 'utils/actions/facility'
import { autoConfirmStop } from 'utils/actions/shipmentStop'
import { log } from 'utils/errorLogger'
import { getUrlParams } from 'utils/getUrlParams'
import { MainStopCard } from './MainStopCard'
import { OtherStopsCard } from './OtherStopsCard'

/**
 * @deprecated Please delete this file when mpNewSchedulerPage got turned on in prod
 */
export const DeprecatedSchedule = React.memo(({ location }) => {
  const dispatch = useDispatch()
  const state = useSelector(
    ({ schedule_stops: { confirmSuccess, errors, loadedSchedule, stops } }) => ({
      stops,
      errors,
      loadedSchedule,
      confirmSuccess,
    })
  )
  const { confirmSuccess, errors, loadedSchedule, stops } = state
  const {
    appointment_type: apptType,
    auto_confirm_token: autoConfirmToken,
    end_at_time: endAtTime,
    facility_uuid: facilityUuid,
    shipper_application_id: shipperId,
    start_at_time: startAtTime,
    stop_type: stopType,
  } = getUrlParams(location.search)

  const { mainStop, otherStops } = stops || {}
  const missingParams = !autoConfirmToken || !shipperId || !facilityUuid || !startAtTime

  // If we haven't loaded the data from the schedule_stops endpoint, do so
  useEffect(() => {
    if (!mainStop && !otherStops && !errors && !loadedSchedule && !missingParams) {
      dispatch(getScheduleStops({ autoConfirmToken, shipperId, stopType, facilityUuid }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the mainStop isn't confirmed and isn't deprecated ("expired"), then do the auto confirmation
  useEffect(() => {
    if (
      mainStop &&
      !mainStop.confirmed &&
      mainStop.appointmentStatus !== 'confirmed' &&
      !mainStop.isDeprecated
    ) {
      const { stopId } = mainStop
      dispatch(autoConfirmStop(stopId, autoConfirmToken, startAtTime, endAtTime))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainStop])

  // Log error and stop when there is an error
  useEffect(() => {
    if (errors) {
      log('quick_requests_frontend', errors, mainStop)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors])

  return (
    <div className='container'>
      <div className='row tc xs-shift-3 col-xs-6'>
        <HeaderLogo logoStyle='black' />
        {!loadedSchedule && <Spinner position='absolute' />}
        {loadedSchedule && (
          <div className='tl'>
            {missingParams && (
              <InlineMessage
                message='Missing required parameter. Unable to make changes. Please reply to the email we sent you.'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
              />
            )}
            {!missingParams && !mainStop && (
              <InlineMessage
                message='Could not locate stop. Please reply to the email we sent you.'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
              />
            )}
            {errors && (
              <InlineMessage
                message='We encountered an error scheduling this shipment, please reply to the email we sent you.'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
              />
            )}
            {mainStop && (
              <MainStopCard
                mainStop={mainStop}
                errors={errors}
                stopType={stopType}
                apptType={apptType}
                autoConfirmToken={autoConfirmToken}
                confirmSuccess={confirmSuccess}
              />
            )}
            {otherStops?.length > 0 && (
              <OtherStopsCard otherStops={otherStops} apptType={apptType} stopType={stopType} />
            )}
          </div>
        )}
      </div>
    </div>
  )
})

DeprecatedSchedule.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
}
